import React from 'react';
import cn from 'classnames';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import feedBg from 'src/assets/feed-bg.png';
import Controls from 'src/components/common/Controls';
import Header from 'src/components/common/Header';
import * as routes from 'src/constants/routes';
import { stats as statsApi } from 'src/store/api/stats';
import Scroller, { scrollTo } from 'src/utils/scroller';

import Hero from './Hero';
import HubsFeed from './HubsFeed';
import css from './MainMobile.module.scss';
import { useHeroPlayKey } from './helpers';

interface Props {
  className?: string;
}

const MainMobile: React.FC<Props> = props => {
  const { className = '' } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const hubsFeedRef = React.useRef<HTMLDivElement>(null);
  const heroRef = React.useRef<HTMLDivElement>(null);
  const scrollerRef = React.useRef<Scroller>(new Scroller());
  const scrollLimitsRef = React.useRef<{ min: number; max: number }>({ min: 0, max: 0 });
  const currentScrollRef = React.useRef(0);
  const mainPageRouteMatch = useMatch({ path: routes.ROOT });
  const location = useLocation();
  const navigate = useNavigate();
  const page = new URLSearchParams(location.search).get('page');
  const prevPage = React.useRef(page);
  const [isSliderActive, setIsSliderActive] = React.useState(!!page);
  const isMainPageRouteMatched = !!mainPageRouteMatch;

  const isHeroVisible = !page;
  const { playKey: heroPlayKey, restart: restartHeroAnimation } = useHeroPlayKey(isHeroVisible);

  const { data: statsData } = statsApi.endpoints.getGlobalStats.useQuery();
  const hubsStatistics = statsData?.success ? statsData.data : undefined;

  // Scroll limits:
  React.useEffect(() => {
    const containerHeight = containerRef.current?.offsetHeight || 0;
    const contentHeight = contentRef.current?.offsetHeight || 0;
    const scroller = scrollerRef.current;

    scrollLimitsRef.current.min = 0;
    scrollLimitsRef.current.max = contentHeight - containerHeight;

    if (!contentRef.current) {
      throw new Error('Unable to initialize scroller: container is null');
    }

    scroller.init({ container: contentRef.current });

    return () => {
      scroller.destroy();
    };
  }, []);

  // Handle content height change:
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      const contentHeight = contentRef.current?.offsetHeight || 0;
      const containerHeight = containerRef.current?.offsetHeight || 0;

      scrollLimitsRef.current.max = contentHeight - containerHeight;
      if (page) scrollTo(contentRef.current, scrollLimitsRef.current.max, { duration: 0 });
    });

    observer.observe(contentRef.current as Node, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [page]);

  // Custom scrolling:
  React.useEffect(() => {
    const page = new URLSearchParams(location.search).get('page');
    if (page || !mainPageRouteMatch || !containerRef.current || !contentRef.current || !hubsFeedRef.current) {
      if (page) setIsSliderActive(true);
      return;
    }

    const scroller = scrollerRef.current;
    let scrolledDistance = 0;
    let touchStartY = 0;
    let touchEndY = 0;

    scroller.subscribe('touch', onTouchEvent, MAIN_PAGE_SCROLLER_SUBJECT);
    scroller.subscribe('touchScroll', onScrollEvent, MAIN_PAGE_SCROLLER_SUBJECT);

    function onScrollEvent(deltaY: number) {
      scrolledDistance += deltaY;
      scrollTo(contentRef.current, scrolledDistance, { duration: 0 });
    }

    function onTouchEvent(e: TouchEvent) {
      switch (e.type) {
        case 'touchstart':
          scrolledDistance = 0;
          touchStartY = e.touches[0].clientY;
          touchEndY = e.touches[0].clientY;
          break;

        case 'touchmove':
          touchEndY = e.touches[0].clientY;
          break;

        case 'touchend': {
          const minDistance = 60;
          const absDistance = Math.abs(scrolledDistance);

          if (touchEndY > touchStartY && absDistance > 1.75 * minDistance) {
            // do nothing
          }

          if (touchEndY < touchStartY && absDistance > minDistance) {
            const urlParams = new URLSearchParams(location.search);
            urlParams.set('page', '1');
            navigate(`${routes.ROOT}?${urlParams.toString()}`);
            return;
          }

          scrollTo(contentRef.current, scrollLimitsRef.current.min, {
            duration: 0.4,
            ease: 'back.out(1.0)',
          });
          break;
        }
      }
    }

    return () => {
      scroller.unsubscribe('touch', MAIN_PAGE_SCROLLER_SUBJECT);
      scroller.unsubscribe('touchScroll', MAIN_PAGE_SCROLLER_SUBJECT);
    };
  }, [mainPageRouteMatch, location.search]);

  React.useEffect(() => {
    if (!isMainPageRouteMatched) return;

    const scrollLimits = scrollLimitsRef.current;

    if (page) {
      const scroller = scrollerRef.current;
      scroller.unsubscribe('wheelScroll', MAIN_PAGE_SCROLLER_SUBJECT);
      scroller.unsubscribe('touchScroll', MAIN_PAGE_SCROLLER_SUBJECT);

      scrollTo(contentRef.current, scrollLimits.max, {
        duration: prevPage.current !== page ? 0.4 : 0,
        ease: 'back.out(1.0)',
        onComplete: () => {
          setIsSliderActive(true);
        },
      });

      currentScrollRef.current = scrollLimits.max;
      prevPage.current = page;
    } else {
      scrollTo(contentRef.current, 0, {
        duration: prevPage.current !== page ? 0.4 : 0,
        ease: 'back.out(1.0)',
        onStart: () => {
          setIsSliderActive(false);
        },
      });

      currentScrollRef.current = 0;
      prevPage.current = page;
    }
  }, [page, isMainPageRouteMatched]);

  const onDeactivateSlider = React.useCallback(() => {
    setIsSliderActive(false);
    prevPage.current = '0';
  }, []);

  const onScrollDownClick = () => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('page', '1');
    navigate(`${routes.ROOT}?${urlParams.toString()}`);
  };

  return (
    <>
      <div
        className={cn(css.mainMobile, isSliderActive && css.sliderActive, className)}
        ref={containerRef}
        style={{ backgroundImage: `url(${feedBg})` }}
      >
        <Header className={css.header} />
        <Controls />
        <div className={css.content} ref={contentRef}>
          <div ref={heroRef} className={css.heroWrap}>
            <Hero
              playKey={heroPlayKey}
              isPlaying={isHeroVisible}
              onAnimationComplete={restartHeroAnimation}
              onScrollDownClick={onScrollDownClick}
              connectedToday={hubsStatistics?.totalPrayingUsersCount ?? 0}
              totalPrayDuration={hubsStatistics?.totalPrayerDurationAllUsers}
            />
          </div>
          <HubsFeed
            className={css.hubsFeed}
            scroller={scrollerRef.current}
            active={isSliderActive}
            containerRef={hubsFeedRef}
            onDeactivate={onDeactivateSlider}
          />
        </div>
      </div>
    </>
  );
};

const MAIN_PAGE_SCROLLER_SUBJECT = 'mainPageScroller';

export default MainMobile;
